import * as UI from "@iyk/ui"

import { Email } from "@iyk/core"
import { ActionResponse, Cookie, LoaderResponse } from "@iyk/remix"
import type { MetaFunction } from "@remix-run/node"
import { useFetcher } from "@remix-run/react"
import { Data, Effect, Option, Schema } from "effect"
import { OAuth } from "../../core/auth/oauth.ts"
import { Influencer } from "../../core/influencers/influencer.ts"
import { Remix } from "../../server/main.ts"
import { Assets } from "../../shared/assets.ts"
import { BasicLayout } from "../../shared/basic-layout.tsx"
import { COMPANY_NAME } from "../../shared/constants.ts"
import { OpenGraph } from "../../shared/open-graph.ts"
import { InfluencerRoutes } from "../influencer-routes.ts"
import { Header } from "./ui/header.tsx"

export const meta: MetaFunction<typeof loader> = ({ location }) => {
  const title = `Login | ${COMPANY_NAME}`
  return [
    { title },
    ...OpenGraph.generateProperties({
      title,
      pathname: location.pathname,
      type: "website",
    }),
  ]
}

export const loader = Remix.loader.handler(({ capabilities: { influencerSessions } }) =>
  influencerSessions.authenticated.pipe(
    Effect.option,
    Effect.map((session) =>
      Option.isSome(session)
        ? LoaderResponse.TemporaryRedirect(InfluencerRoutes.home)
        : LoaderResponse.Ok(null),
    ),
  ),
)

const EmailForm = Schema.Struct({
  _tag: Schema.Literal("Otp"),
  email: Email.annotations({ message: () => "Please enter a valid email" }),
})

const OAuthForm = Schema.Struct({
  _tag: Schema.Literal("OAuth"),
  provider: OAuth.Provider.schema.fields._tag,
})

const LoginFormSchema = Schema.Union(EmailForm, OAuthForm)
type LoginFormSchema = typeof LoginFormSchema.Type
const { $match: matchLoginForm } = Data.taggedEnum<LoginFormSchema>()

export const action = Remix.action
  .formData(LoginFormSchema)
  .handler(({ capabilities: { influencers }, body, cookieJar }) =>
    matchLoginForm({
      Otp: (body) =>
        Effect.gen(function* () {
          yield* influencers.createOtp(body.email)

          yield* cookieJar.set(
            Cookie.make("verificationEmail", body.email, {
              path: "/",
              maxAge: Influencer.Otp.expiration,
            }),
          )

          return ActionResponse.TemporaryRedirect(InfluencerRoutes.verification)
        }),
      OAuth: (body) =>
        Effect.gen(function* () {
          const provider = OAuth.Provider[body.provider]()
          const returnTo = OAuth.ReturnTo.unsafeFrom(InfluencerRoutes.home)
          const url = yield* influencers.createOAuthUrl(provider, returnTo)
          return ActionResponse.TemporaryRedirect(url.href)
        }),
    })(body).pipe(
      Effect.catchTags({
        "Influencer/NotFound": () => ActionResponse.Error("Email does not have access"),
      }),
    ),
  )

export default function InfluencerLoginRoute() {
  const loginFetcher = useFetcher<typeof action>()

  const isEmailFormLoading =
    loginFetcher.state === "submitting" && loginFetcher.formData?.get("_tag") === "Otp"
  const isOAuthFormLoading =
    loginFetcher.state === "submitting" && loginFetcher.formData?.get("_tag") === "OAuth"

  const errorMessage = loginFetcher.data?._tag === "Error" ? loginFetcher.data.error : undefined

  return (
    <BasicLayout>
      <div className="grid grid-rows-[auto_1fr] h-full">
        <Header />
        <div className="md:w-[358px] mx-auto self-center">
          <h1 className="text-base-title mb-6 text-center">Log in</h1>
          <loginFetcher.Form method="post" className="flex flex-col gap-3">
            <input type="hidden" name="_tag" value="Otp" />

            <UI.Field name="email" required validationMessage={errorMessage}>
              <UI.Textbox type="email" autoComplete="email" placeholder="Email" />
              <UI.Validity />
            </UI.Field>

            <UI.Button
              type="submit"
              kind="solid"
              size="md"
              className="w-full"
              disabled={isEmailFormLoading}
            >
              {isEmailFormLoading ? <UI.Icon.LoadingSpinner /> : "Continue with email"}
            </UI.Button>
          </loginFetcher.Form>

          <div className="flex items-center gap-2 my-3">
            <hr className="flex-1 text-gray-4" />
            <span className="text-sm text-gray-9">or</span>
            <hr className="flex-1 text-gray-4" />
          </div>

          <loginFetcher.Form method="post" className="flex flex-col gap-3">
            <input type="hidden" name="_tag" value="OAuth" />

            <UI.Button
              type="submit"
              name="provider"
              value="Google"
              kind="outline"
              size="md"
              className="w-full"
              disabled={isOAuthFormLoading}
            >
              <UI.Icon.Google />
              Continue with Google
            </UI.Button>
          </loginFetcher.Form>

          <p className="text-xs text-gray-11 mt-6 text-center px-7">
            By signing up, you are creating a {COMPANY_NAME} account and agree to the{" "}
            <a href={Assets.documents.termsOfService} target="_blank" className="underline">
              Terms and Conditions
            </a>
            {" & "}
            <a href={Assets.documents.privacyPolicy} target="_blank" className="underline">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </BasicLayout>
  )
}
